import { useEffect, useMemo, useState } from 'react';

import { capitalize } from 'lodash-es';

import {
  IBackendCartEntries,
  ICartEntry,
  ICombo,
  IComboSlot,
  IItemOption,
  IPickerOption,
  MenuObject,
} from '@rbi-ctg/menu';
import { useMenuContext } from 'state/menu';
import { useUIContext } from 'state/ui';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';
import { maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';

interface UiPatternOption {
  _id?: string;
  uiPattern?: ComboSlotUiPattern | null;
}
interface UiPatternOptionMap {
  [id: string]: UiPatternOption;
}

interface ItemDescription {
  name: string;
  quantity: number;
  price: number;
}

type ItemDescriptionMap = { [key: string]: ItemDescription };

const getDescriptionMap = ({
  cartEntries = [],
  quantity,
  itemDescriptionMap,
  shouldHideCartEntry,
  shouldIncludeModifiers,
  computePrice,
}: {
  cartEntries?: ICartEntry['children'] | IBackendCartEntries['children'];
  quantity: number;
  itemDescriptionMap: ItemDescriptionMap;
  shouldHideCartEntry: (c: ICartEntry | IBackendCartEntries) => boolean;
  shouldIncludeModifiers: (c: ICartEntry | IBackendCartEntries) => boolean;
  computePrice: (c: ICartEntry | IBackendCartEntries) => number;
}): ItemDescriptionMap => {
  for (const cartEntry of cartEntries) {
    if (shouldHideCartEntry(cartEntry)) {
      continue;
    }

    if (shouldIncludeModifiers(cartEntry)) {
      const itemName = (cartEntry.name || '').split(' ').map(capitalize).join(' ');
      const price = computePrice(cartEntry);
      const quantityToAdd = quantity * cartEntry.quantity;

      if (itemDescriptionMap[itemName]) {
        // Add up if existing
        itemDescriptionMap[itemName] = {
          ...itemDescriptionMap[itemName],
          quantity: itemDescriptionMap[itemName].quantity + quantityToAdd,
          price: itemDescriptionMap[itemName].price + price,
        };
      } else {
        // Create new entry if empty
        itemDescriptionMap[itemName] = {
          name: itemName,
          quantity: quantityToAdd,
          price,
        };
      }
    }

    // Recursively add children
    itemDescriptionMap = {
      ...itemDescriptionMap,
      ...getDescriptionMap({
        cartEntries: cartEntry.children,
        quantity,
        itemDescriptionMap,
        shouldHideCartEntry,
        shouldIncludeModifiers,
        computePrice,
      }),
    };
  }
  return itemDescriptionMap;
};

/**
 * This hook takes a cart entry item and returns description
 * composed of comboSlot & itemOption selections.
 *
 * Note: includeModifiers also controls if we show comboSlot items.
 */
export function useComposeDescription(
  item: ICartEntry | IBackendCartEntries,
  options: {
    includeModifiers?: boolean;
    includeQuantity?: boolean;
  } = {}
): string[] {
  const { includeModifiers = false, includeQuantity = false } = options;

  const { menuObject } = useCartEntryMenuObject({ cartEntry: item });
  const { formatCurrencyForLocale } = useUIContext();

  return useMemo(() => {
    // Function to compute Pricing
    const quantity = item.quantity;
    const itemPricesToIgnore = getMenuObjectPricesToIgnore(menuObject);
    const computePrice = (cartEntry: ICartEntry | IBackendCartEntries): number => {
      const shouldIgnorePrice = itemPricesToIgnore.has(cartEntry.sanityId);
      return shouldIgnorePrice ? 0 : (cartEntry.price || 0) * quantity;
    };

    // Function to determine Visibility
    const uiPatternMap = getMenuObjectUiPatternMap(menuObject);
    const shouldHideCartEntry = (cartEntry: ICartEntry | IBackendCartEntries): boolean => {
      const uiPattern =
        ('uiPattern' in cartEntry ? cartEntry.uiPattern : null) || uiPatternMap[cartEntry.sanityId];
      return (
        (cartEntry.type === CartEntryType.comboSlot && uiPattern === ComboSlotUiPattern.HIDDEN) ||
        uiPattern === ComboSlotUiPattern.COLLAPSED
      );
    };

    // Function to determine Modifiers criteria
    const shouldIncludeModifiers = (cartEntry: ICartEntry | IBackendCartEntries): boolean => {
      return (
        cartEntry.type === CartEntryType.item ||
        (includeModifiers && cartEntry.type === CartEntryType.itemOptionModifier)
      );
    };

    // Init recursion
    const itemDescriptionMap = getDescriptionMap({
      cartEntries: item.children,
      quantity,
      itemDescriptionMap: {},
      shouldHideCartEntry,
      shouldIncludeModifiers,
      computePrice,
    });

    // Format results
    return Object.values(itemDescriptionMap).map(itemDescription => {
      const quantityLabel = includeQuantity ? itemDescription.quantity : '';
      const nameLabel = itemDescription.name;
      const priceLabel =
        itemDescription.price !== 0 ? formatCurrencyForLocale(itemDescription.price) : '';
      const priceSign = priceLabel && itemDescription.price > 0 ? '+' : '';

      return `${quantityLabel} ${nameLabel} ${priceSign}${priceLabel}`;
    });
  }, [
    formatCurrencyForLocale,
    includeModifiers,
    includeQuantity,
    item.children,
    item.quantity,
    menuObject,
  ]);
}

// TODO: FPF - Create separate files for this helpers

const useCartEntryMenuObject = ({ cartEntry }: { cartEntry: ICartEntry | IBackendCartEntries }) => {
  const { asyncGetMenuObject } = useMenuContext() || {};
  const [menuObject, setMenuObject] = useState<MenuObject | null>(null);

  const menuIdentifier = useMemo(() => maybeMapCartEntryToMenuObjectIdentifier(cartEntry), [
    cartEntry,
  ]);

  useEffect(() => {
    let mounted = true;

    // If a mapping cant be done, return cartEntry unmapped
    if (!menuIdentifier || !asyncGetMenuObject) {
      setMenuObject(null);
      return;
    }

    const getMenuObject = async () => {
      const { data } = await asyncGetMenuObject(menuIdentifier);
      if (data && mounted) {
        setMenuObject(data);
      }
    };

    getMenuObject();

    return () => {
      mounted = false;
    };
  }, [asyncGetMenuObject, menuIdentifier]);

  return { menuObject };
};

const getMenuObjectPricesToIgnore = (menuObject: MenuObject | null) => {
  const pricesToIgnore = new Set<string>();
  const mergedOptions = [
    ...(menuObject?.options || []),
    ...((menuObject as ICombo)?.mainItem?.options || []),
  ];
  mergedOptions.forEach((option: IComboSlot | IItemOption | MenuObject | IPickerOption) => {
    if ((option as IItemOption).allowMultipleSelections) {
      (option as IItemOption).options.forEach(opt => pricesToIgnore.add(opt._key));
    }
  });
  return pricesToIgnore;
};

const getMenuObjectUiPatternMap = (menuObject: MenuObject | null) => {
  const uiPatternMap = ((menuObject?.options as UiPatternOption[]) || []).reduce((acc, option) => {
    if (option._id && option.uiPattern) {
      acc[option._id] = option;
    }
    return acc;
  }, {} as UiPatternOptionMap);
  return uiPatternMap;
};
